import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { ChevronRight, HeartHandshake, Sparkles, Users, MapPin, CalendarClock, FileText, SearchX } from 'lucide-react';
import { useCockpitTitle } from '../components/EmployerCockpitLayout';
import { getB2bAcademy, getB2bZoek, getB2bTraining, accepteerOfferte, weigerOfferte } from '../services/b2bAcademyApi';
import type { B2bTraining, Rfq } from '../services/b2bAcademyApi';
import { B2bRij } from '../components/b2bAcademy/B2bRij';
import { B2bTegel } from '../components/b2bAcademy/B2bTegel';
import { B2bDetailModal } from '../components/b2bAcademy/B2bDetailModal';
import { B2bZoekbalk } from '../components/b2bAcademy/B2bZoekbalk';
import { B2bActieveFilters, B2bFilterbalk } from '../components/b2bAcademy/B2bFilterbalk';
import { b2bFiltersUitParams, heeftB2bFilters, paramsMetB2bFilters, type B2bFilters } from '../components/b2bAcademy/b2bFilters';
import { LOCATIE_OPTIES, PLANNING_OPTIES, STATUS_INFO, datumKort, eur, prijs } from '../components/b2bAcademy/b2bStijl';
import { UitvoerdatumBevestiging, uitvoerdatumStap } from '../components/b2bAcademy/UitvoerdatumBevestiging';

function foutMelding(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const msg = (error.response?.data as { message?: string } | undefined)?.message;
    if (msg) return msg;
  }
  return fallback;
}

/** Financiële breakdown (spec §5C) + [🤝 Accepteer Offerte & Plan Data] zodra de status OFFER_SENT is. */
const RfqKaart: React.FC<{ rfq: Rfq; uitgelicht: boolean }> = ({ rfq, uitgelicht }) => {
  const { t } = useTranslation('pages');
  const queryClient = useQueryClient();
  const status = STATUS_INFO[rfq.status];
  const ververs = () => queryClient.invalidateQueries({ queryKey: ['b2b-academy'] });
  const accepteer = useMutation({ mutationFn: () => accepteerOfferte(rfq.id), onSuccess: ververs });
  const weiger = useMutation({ mutationFn: () => weigerOfferte(rfq.id), onSuccess: ververs });
  const locatie = LOCATIE_OPTIES.find((o) => o.key === rfq.locatie_voorkeur);
  const planning = PLANNING_OPTIES.find((o) => o.key === rfq.planning);
  const bezig = accepteer.isPending || weiger.isPending;

  return (
    <li id={`rfq-${rfq.id}`} className={`rounded-2xl border p-4 ${uitgelicht ? 'border-amber-400/60 bg-amber-50' : 'border-gray-200 bg-white'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold text-gray-900">{rfq.titel}</p>
          <p className="text-xs text-gray-500">
            {rfq.organisatie_naam} · {t('b2bAcademy.rfq.aangevraagd', { defaultValue: 'aangevraagd {{datum}}', datum: datumKort(rfq.created_at) })}
            {rfq.rij_label ? ` · ${rfq.rij_label}` : ''}
          </p>
        </div>
        <span className={`inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-xs font-medium ${status.cls}`}>{t(`b2bAcademy.status.${rfq.status}`, { defaultValue: status.label })}</span>
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
        <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />{rfq.deelnemers} {t('b2bAcademy.rfq.deelnemers', { defaultValue: 'deelnemers' })}{rfq.afdeling ? ` · ${rfq.afdeling}` : ''}</span>
        <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />{locatie ? t(`b2bAcademy.locatie.${locatie.key}`, { defaultValue: locatie.label }) : rfq.locatie_voorkeur}</span>
        <span className="inline-flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />{planning ? t(`b2bAcademy.planning.${planning.key}`, { defaultValue: planning.label }) : rfq.planning}</span>
      </div>

      {rfq.status === 'RFQ_SUBMITTED' && (
        <p className="mt-3 text-sm text-gray-500">{t('b2bAcademy.rfq.wachtOpOfferte', { defaultValue: 'De opleider is gesignaleerd en stelt de offerte op. U ontvangt een melding zodra deze klaarstaat.' })}</p>
      )}

      {(rfq.status === 'OFFER_SENT' || rfq.status === 'OFFER_ACCEPTED') && rfq.marktprijs !== null && (
        <div className="mt-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t('b2bAcademy.rfq.breakdown', { defaultValue: 'Transparante financiële breakdown' })}</h4>
          <dl className="mt-2 space-y-1.5 text-sm">
            <div className="flex justify-between gap-3">
              <dt className="text-gray-500">{t('b2bAcademy.rfq.projectprijs', { defaultValue: 'Normale projectprijs' })}</dt>
              <dd className="text-gray-600"><s>{prijs(rfq.marktprijs)}</s></dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="inline-flex items-center gap-1 text-emerald-600"><Sparkles className="h-3.5 w-3.5" aria-hidden="true" />{t('b2bAcademy.rfq.voordeel', { defaultValue: 'BeLagom B2B Voordeel (5%)' })}</dt>
              <dd className="whitespace-nowrap text-emerald-600">− {prijs(rfq.korting_bedrag ?? 0)}</dd>
            </div>
            <div className="flex justify-between gap-3 border-t border-gray-200 pt-1.5 text-base font-bold text-gray-900">
              <dt>{t('b2bAcademy.rfq.postPay', { defaultValue: 'Achteraf te factureren bedrag (Post-Pay)' })}</dt>
              <dd className="whitespace-nowrap">{prijs(rfq.offertewaarde ?? 0)}</dd>
            </div>
          </dl>
          {rfq.offerte_toelichting && <p className="mt-2 whitespace-pre-line text-xs text-gray-600">{rfq.offerte_toelichting}</p>}
          <p className="mt-1 text-[11px] text-gray-400">{t('b2bAcademy.rfq.exclBtw', { defaultValue: 'Bedragen excl. btw.' })}</p>
        </div>
      )}

      {/* Besluit 5: uitvoerdatum bevestigen of betwisten vóór de verzamelfactuur */}
      <UitvoerdatumBevestiging rfq={rfq} />

      {(accepteer.isError || weiger.isError) && (
        <p className="mt-2 text-sm text-red-600" role="alert">{foutMelding(accepteer.error ?? weiger.error, t('b2bAcademy.rfq.fout', { defaultValue: 'De actie is niet gelukt. Probeer het opnieuw.' }))}</p>
      )}

      {rfq.status === 'OFFER_SENT' && (
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" onClick={() => accepteer.mutate()} disabled={bezig} className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm hover:brightness-110 disabled:opacity-60">
            <HeartHandshake className="h-4 w-4" aria-hidden="true" />
            {t('b2bAcademy.rfq.accepteer', { defaultValue: '🤝 Accepteer Offerte & Plan Data' })}
          </button>
          <button type="button" onClick={() => weiger.mutate()} disabled={bezig} className="rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-600 hover:border-gray-400 hover:text-gray-900 disabled:opacity-60">
            {t('b2bAcademy.rfq.wijsAf', { defaultValue: 'Offerte afwijzen' })}
          </button>
        </div>
      )}
    </li>
  );
};

/**
 * B2B Netflix Academy voor opdrachtgevers op de lichte BeLagom-achtergrond: links de
 * filter- & sorteer-sidebar (B2B Discovery Engine), rechts de streaming-rijen (🔥 Populair op
 * uw kostenplaats + de strategische rijen) of het resultatengrid, de donkerblauwe vensters,
 * het lichte Informatie-scherm met contentmatrix + RFQ-wizard en de offerte-status.
 */
export const EmployerB2bAcademyPage: React.FC = () => {
  const { t } = useTranslation('pages');
  useCockpitTitle(t('b2bAcademy.titel', { defaultValue: 'Opleidingen & Workshops' }), t('b2bAcademy.subtitel', { defaultValue: 'Incompany-trajecten met 5% BeLagom Collectief Voordeel' }));
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => b2bFiltersUitParams(searchParams), [searchParams]);
  const uitgelichtRfq = searchParams.get('rfq');

  const [zoek, setZoek] = useState(filters.q ?? '');
  const [open, setOpen] = useState<{ training: B2bTraining; wizard: boolean } | null>(null);
  const openDetail = (training: B2bTraining) => setOpen({ training, wizard: false });
  const boek = (training: B2bTraining) => setOpen({ training, wizard: true });

  // react-router v7 verwerkt setSearchParams in een transition: altijd de functionele updater
  // gebruiken (en een ref voor de laatste filters), anders overschrijft een debounce-timer met
  // een verouderde closure zojuist gezette params (bijv. ?rij= na een suggestie-chip).
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const zetFilters = (next: B2bFilters) =>
    setSearchParams((prev) => {
      // Alleen de door de aanroeper gewijzigde sleutels toepassen op de actuele URL.
      const actueel = b2bFiltersUitParams(prev);
      const gezien = filtersRef.current;
      const merged: B2bFilters = { ...actueel };
      for (const key of Object.keys(next) as Array<keyof B2bFilters>) {
        if (JSON.stringify(next[key]) !== JSON.stringify(gezien[key])) (merged as unknown as Record<string, unknown>)[key] = next[key];
      }
      return paramsMetB2bFilters(prev, merged);
    }, { replace: true });
  const patch = (p: Partial<B2bFilters>) => setSearchParams((prev) => paramsMetB2bFilters(prev, { ...b2bFiltersUitParams(prev), ...p }), { replace: true });

  // Typen → na 350 ms in de URL (?q=) → grid. Extern gewijzigde q (badge weggeklikt) → invoer volgt.
  useEffect(() => {
    const timer = setTimeout(() => {
      const q = zoek.trim() || null;
      if (q !== filtersRef.current.q) patch({ q });
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoek]);
  useEffect(() => {
    setZoek((huidig) => ((filters.q ?? '') === huidig.trim() ? huidig : filters.q ?? ''));
  }, [filters.q]);

  const academyQuery = useQuery({ queryKey: ['b2b-academy'], queryFn: getB2bAcademy, refetchInterval: 60_000 });
  const filterActief = heeftB2bFilters(filters);
  const zoekQuery = useQuery({
    queryKey: ['b2b-academy-zoek', filters],
    queryFn: () => getB2bZoek(filters),
    enabled: filterActief,
    placeholderData: (vorige) => vorige,
  });

  useEffect(() => {
    if (uitgelichtRfq && academyQuery.isSuccess) document.getElementById(`rfq-${uitgelichtRfq}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [uitgelichtRfq, academyQuery.isSuccess]);

  const data = academyQuery.data;
  const rijenInfo = useMemo(() => data?.rijen_info ?? [], [data?.rijen_info]);
  const rijen = data?.rijen ?? [];
  const aanbevolen = data?.aanbevolen ?? [];
  const rfqs = data?.rfqs ?? [];
  const teBevestigen = rfqs.filter((r) => uitvoerdatumStap(r) === 'bevestigen').length;
  const wkr = zoekQuery.data?.meta.wkr ?? data?.wkr;
  const openRfqVoor = (trainingId: number) => rfqs.find((r) => r.training_id === trainingId && (r.status === 'RFQ_SUBMITTED' || r.status === 'OFFER_SENT')) ?? null;

  const openTraject = async (id: number) => {
    const bekend = zoekQuery.data?.trainingen.find((x) => x.id === id) ?? [...aanbevolen, ...rijen.flatMap((r) => r.trainingen)].find((x) => x.id === id);
    if (bekend) {
      openDetail(bekend);
      return;
    }
    try {
      openDetail(await getB2bTraining(id));
    } catch {
      patch({ q: zoek.trim() || null });
    }
  };

  const actieveRij = rijenInfo.find((r) => r.key === filters.rij) ?? null;
  const resultaten = zoekQuery.data?.trainingen ?? [];
  const resultTitel = actieveRij
    ? `${actieveRij.emoji} ${actieveRij.badge}`
    : filters.q
      ? t('b2bAcademy.resultatenVoor', { defaultValue: 'Resultaten voor "{{zoek}}"', zoek: filters.q })
      : t('b2bAcademy.alleTrajecten', { defaultValue: 'Alle incompany-trajecten' });
  const toonGrid = filterActief && (zoekQuery.isSuccess || zoekQuery.isError);
  const wkrTip = filters.fiscaal.includes('wkr') || filters.rij === 'passie';

  return (
    <div className="min-h-full bg-gray-50">
      <div className="mx-auto max-w-7xl space-y-6 px-4 py-6 pb-16 sm:px-6 lg:px-8">
        <header>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">{t('b2bAcademy.eyebrow', { defaultValue: 'Employer cockpit · B2B Academy' })}</p>
          <h1 className="mt-1 text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl">{t('b2bAcademy.kop', { defaultValue: 'Incompany opleidingen & workshops' })}</h1>
          <p className="mt-1 text-sm text-gray-500">{t('b2bAcademy.intro', { defaultValue: 'Strategische rijen, transparante fiscale status en een digitale offerte-aanvraag in vier stappen. Altijd 5% BeLagom Collectief Voordeel, achteraf gefactureerd.' })}</p>
        </header>

        {academyQuery.isError && (
          <p className="text-sm text-red-600" role="alert">{foutMelding(academyQuery.error, t('b2bAcademy.laadfout', { defaultValue: 'De incompany-catalogus kon niet worden geladen.' }))}</p>
        )}

        <div className="space-y-6 lg:grid lg:grid-cols-[17rem_minmax(0,1fr)] lg:gap-8 lg:space-y-0">
          <aside className="lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:self-start lg:overflow-y-auto">
            <B2bFilterbalk filters={filters} rijen={rijenInfo} wkr={wkr} onChange={zetFilters}>
              <B2bZoekbalk
                waarde={zoek}
                onChange={setZoek}
                onZoek={(q) => patch({ q: q || null })}
                onKiesTraject={openTraject}
                onKiesOpleider={(naam) => { setZoek(naam); patch({ q: naam }); }}
                onKiesRij={(rij) => { setZoek(''); patch({ q: null, rij }); }}
              />
            </B2bFilterbalk>
          </aside>

          <div className="min-w-0 space-y-8">
            <B2bActieveFilters filters={filters} rijen={rijenInfo} onChange={zetFilters} />

            {toonGrid && (
              <section className="space-y-4">
                <div className="flex items-center justify-between gap-2">
                  <h2 className="text-lg font-bold text-gray-900">
                    {resultTitel}
                    {zoekQuery.data && <span className="font-normal text-gray-400"> ({zoekQuery.data.meta.aantal})</span>}
                    {zoekQuery.isFetching && <span className="ml-2 text-xs font-normal text-gray-400">…</span>}
                  </h2>
                  <button type="button" onClick={() => { setZoek(''); zetFilters({ ...filters, q: null, rij: null, vorm: null, fiscaal: [], locatie: [], groep: null, alles: false }); }} className="text-sm font-medium text-sky-600 hover:text-sky-800">
                    {t('b2bAcademy.wisFilters', { defaultValue: 'Alles tonen' })}
                  </button>
                </div>
                {actieveRij && (
                  <div className="-mt-2 rounded-xl border border-gray-200 bg-white p-3 text-xs text-gray-600">
                    <p><strong className="text-gray-900">{t('b2bAcademy.aanbod', { defaultValue: 'Aanbod:' })}</strong> {actieveRij.aanbod}</p>
                    <p className="mt-1"><strong className="text-gray-900">{t('b2bAcademy.doel', { defaultValue: 'Organisatie-doel (ROI):' })}</strong> {actieveRij.doel}</p>
                    <p className={`mt-1 ${actieveRij.fiscaal === 'wkr' ? 'text-orange-700' : 'text-emerald-700'}`}>{actieveRij.fiscaal === 'wkr' ? '⚠️' : '💸'} {actieveRij.fiscaal_label}</p>
                  </div>
                )}
                {zoekQuery.data && zoekQuery.data.meta.synoniem_rijen.length > 0 && filters.q && (
                  <p className="text-xs text-gray-500">
                    {t('b2bAcademy.discovery.synoniemHint', { defaultValue: 'Uw zoekterm is gekoppeld aan:' })}{' '}
                    {zoekQuery.data.meta.synoniem_rijen.map((k) => rijenInfo.find((r) => r.key === k)).filter(Boolean).map((r) => (
                      <button key={r!.key} type="button" onClick={() => patch({ rij: r!.key })} className="mr-1.5 inline-flex items-center gap-1 rounded-full border bg-white px-2 py-0.5 text-[11px] font-semibold text-gray-700 hover:border-gray-400" style={{ borderColor: `${r!.kleur}88` }}>
                        <span aria-hidden="true">{r!.emoji}</span>{r!.badge}
                      </button>
                    ))}
                  </p>
                )}
                {zoekQuery.isError && <p className="text-sm text-red-600" role="alert">{foutMelding(zoekQuery.error, t('b2bAcademy.trainingenFout', { defaultValue: 'Trajecten konden niet worden geladen.' }))}</p>}
                {zoekQuery.isSuccess && resultaten.length === 0 && (
                  <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center">
                    <SearchX className="mx-auto h-8 w-8 text-gray-300" aria-hidden="true" />
                    <p className="mt-3 text-base font-semibold text-gray-900">{t('b2bAcademy.discovery.leegTitel', { defaultValue: 'Geen incompany-trajecten gevonden voor deze specifieke combinatie.' })}</p>
                    <p className="mt-1 text-sm text-gray-500">
                      {wkrTip
                        ? t('b2bAcademy.discovery.leegTipWkr', { defaultValue: "Tip: Vink het filter 'WKR-Belast' uit om onbelaste, gericht vrijgestelde Arbo-interventies te bekijken." })
                        : t('b2bAcademy.discovery.leegTip', { defaultValue: 'Tip: verwijder een filter of zoek op het organisatieprobleem (bijv. "verzuim" of "nachtdienst").' })}
                    </p>
                    {wkrTip && (
                      <button type="button" onClick={() => patch({ fiscaal: filters.fiscaal.filter((f) => f !== 'wkr'), rij: filters.rij === 'passie' ? null : filters.rij })} className="mt-4 rounded-xl border border-emerald-400/40 bg-emerald-400/10 px-4 py-2 text-sm font-semibold text-emerald-800 hover:bg-emerald-400/20">
                        {t('b2bAcademy.discovery.leegKnopWkr', { defaultValue: "Filter 'WKR-Belast' uitzetten" })}
                      </button>
                    )}
                  </div>
                )}
                <div className={`grid grid-cols-1 gap-4 transition-opacity duration-300 sm:grid-cols-2 xl:grid-cols-3 ${zoekQuery.isFetching ? 'opacity-60' : ''}`}>
                  {resultaten.map((tr) => <B2bTegel key={tr.id} training={tr} onOpen={openDetail} onBoek={boek} className="w-full" />)}
                </div>
              </section>
            )}

            {!toonGrid && (
              <div className={`space-y-8 transition-opacity duration-300 ${filterActief ? 'pointer-events-none opacity-20' : ''}`} aria-hidden={filterActief || undefined}>
                {academyQuery.isLoading && (
                  <div className="flex gap-4 overflow-hidden" aria-hidden="true">
                    {[0, 1, 2].map((i) => <div key={i} className="aspect-[16/12] w-[18rem] shrink-0 animate-pulse rounded-xl bg-white" />)}
                  </div>
                )}
                {academyQuery.isSuccess && rijen.length === 0 && aanbevolen.length === 0 && (
                  <p className="rounded-2xl border border-gray-200 bg-white p-5 text-sm text-gray-600">{t('b2bAcademy.catalogusLeeg', { defaultValue: 'De incompany-catalogus wordt nog gevuld. Kom binnenkort terug!' })}</p>
                )}
                {aanbevolen.length > 0 && (
                  <B2bRij
                    id="aanbevolen"
                    titel={t('b2bAcademy.rij.aanbevolen', { defaultValue: 'Populair op uw kostenplaats' })}
                    subtitel={t('b2bAcademy.rij.aanbevolenUitleg', { defaultValue: 'Trajecten in de thema’s die uw organisatie eerder aanvroeg, aangevuld met de meest ingekochte trajecten op BeLagom' })}
                    emoji="🔥"
                    accent="#E5A425"
                    badge={t('b2bAcademy.rij.aanbevolenBadge', { defaultValue: 'Aanbevolen' })}
                    trainingen={aanbevolen}
                    onOpen={openDetail}
                    onBoek={boek}
                    onAlles={() => patch({ sort: 'populair', alles: true })}
                  />
                )}
                {rijen.map((r) => (
                  <B2bRij key={r.key} id={r.key} titel={r.label} subtitel={r.aanbod} emoji={r.emoji} accent={r.kleur} fiscaalLabel={r.fiscaal_label} wkr={r.fiscaal === 'wkr'} trainingen={r.trainingen} onOpen={openDetail} onBoek={boek} onAlles={() => patch({ rij: r.key, alles: false })} />
                ))}
                {rijen.length > 0 && (
                  <button type="button" onClick={() => patch({ alles: true })} className="inline-flex w-full items-center justify-center gap-1 rounded-xl border border-gray-200 bg-white py-3 text-sm font-medium text-gray-700 hover:border-gray-400">
                    {t('b2bAcademy.alleBekijken', { defaultValue: 'Alle incompany-trajecten van A tot Z bekijken' })}
                    <ChevronRight className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              </div>
            )}

            <section>
              <h2 className="mb-3 flex items-center gap-2 text-lg font-bold text-gray-900">
                <FileText className="h-5 w-5 text-sky-600" aria-hidden="true" />
                {t('b2bAcademy.mijnRfqs', { defaultValue: 'Mijn offerte-aanvragen' })}
                {teBevestigen > 0 && (
                  <span className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800">
                    {t('b2bAcademy.teBevestigen', { defaultValue: '{{aantal}} uitvoerdatum te bevestigen', aantal: teBevestigen })}
                  </span>
                )}
              </h2>
              {rfqs.length === 0 ? (
                <p className="rounded-2xl border border-gray-200 bg-white p-4 text-sm text-gray-500">{t('b2bAcademy.geenRfqs', { defaultValue: 'Nog geen offerte-aanvragen. Open een traject en start de digitale offerte-aanvraag.' })}</p>
              ) : (
                <ul className="grid gap-3 md:grid-cols-2">
                  {rfqs.map((r) => <RfqKaart key={r.id} rfq={r} uitgelicht={r.id === uitgelichtRfq} />)}
                </ul>
              )}
              {rfqs.some((r) => r.status === 'OFFER_ACCEPTED') && (
                <p className="mt-2 text-xs text-gray-400">
                  {t('b2bAcademy.totaalGeaccepteerd', { defaultValue: 'Totaal geaccepteerd (post-pay): {{bedrag}}', bedrag: eur.format(rfqs.filter((r) => r.status === 'OFFER_ACCEPTED').reduce((s, r) => s + (r.offertewaarde ?? 0), 0)) })}
                </p>
              )}
            </section>
          </div>
        </div>
      </div>

      {open && <B2bDetailModal key={`${open.training.id}-${open.wizard}`} training={open.training} openRfq={openRfqVoor(open.training.id)} startWizard={open.wizard} onClose={() => setOpen(null)} />}
    </div>
  );
};

export default EmployerB2bAcademyPage;
